import { closestCorners, DndContext, DragOverlay, useDroppable, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { Section, Task } from "@todoer/shared";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { confirmDialog, Menu, promptDialog } from "./overlay.js";
import { AddTaskButton, subtaskCounts, TaskCard, useTaskSensors } from "./TaskList.js";
import { Button, cx, Input } from "./ui.js";

const NONE = "none";

function SortableCard({ task, counts }: { task: Task; counts: { done: number; total: number } }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  return (
    <TaskCard task={task} counts={counts}
      dnd={{ ref: setNodeRef, style: { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.35 : undefined }, attributes, listeners }} />
  );
}

function Column({ id, title, tasks, counts, projectId, sectionId, section, dragging }: {
  id: string; title: string; tasks: Task[]; counts: Map<string, { done: number; total: number }>; projectId: string; sectionId: string | null; section?: Section; dragging: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${id}` });
  return (
    <section aria-label={title} className="flex w-[288px] shrink-0 snap-start flex-col rounded-card bg-surface-sunken p-2 phone:w-[85vw]">
      <header className="flex h-9 items-center gap-2 px-1.5">
        <h3 className="min-w-0 truncate text-heading font-semibold">{title}</h3>
        <span className="tabular text-meta text-text-faint">{tasks.length}</span>
        {section && (
          <span className="ml-auto"><Menu size="sm" label={`${title} actions`} items={[
            { label: "Rename section", icon: Pencil, onSelect: async () => { const n = await promptDialog({ title: "Rename section", label: "Section name", value: section.name }); if (n) void dispatch(cmd("section_update", { id: section.id, name: n })); } },
            { label: "Delete section", icon: Trash2, danger: true, divider: true, onSelect: async () => { if (await confirmDialog({ title: `Delete “${section.name}”?`, body: "The section and its tasks move to Trash.", confirmLabel: "Delete", danger: true })) void dispatch(cmd("section_delete", { id: section.id })); } },
          ]} /></span>
        )}
      </header>
      <div ref={setNodeRef} className={cx("flex min-h-20 flex-1 flex-col gap-2 rounded-control p-0.5 transition-colors duration-150", dragging && "outline-1 -outline-offset-1 outline-dashed outline-border-strong", isOver && "bg-brand-soft outline-brand")}>
        <SortableContext id={id} items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((t) => <SortableCard key={t.id} task={t} counts={counts.get(t.id) ?? { done: 0, total: 0 }} />)}
        </SortableContext>
      </div>
      <div className="mt-1 px-1"><AddTaskButton projectId={projectId} sectionId={sectionId} /></div>
    </section>
  );
}

/** Kanban board: one column per section. Drag cards within or between columns. */
export function Board({ projectId, sections, tasks, allTasks }: { projectId: string; sections: Section[]; tasks: Task[]; allTasks: Task[] }) {
  const sensors = useTaskSensors();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const top = tasks.filter((t) => !t.parentId || !tasks.some((p) => p.id === t.parentId));
  const counts = subtaskCounts(allTasks);
  const columnOf = (sectionId: string | null) => top.filter((t) => (t.sectionId ?? null) === sectionId).sort((a, b) => a.childOrder - b.childOrder);
  const sorted = [...sections].sort((a, b) => a.sectionOrder - b.sectionOrder);

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const id = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || overId === id) return;
    const active = top.find((t) => t.id === id);
    if (!active) return;
    let targetSection: string | null;
    let index: number;
    if (overId.startsWith("col:")) {
      const col = overId.slice(4);
      targetSection = col === NONE ? null : col;
      index = columnOf(targetSection).filter((t) => t.id !== id).length;
    } else {
      const over = top.find((t) => t.id === overId);
      if (!over) return;
      targetSection = over.sectionId ?? null;
      const col = columnOf(targetSection).filter((t) => t.id !== id);
      index = col.findIndex((t) => t.id === overId);
      const sameCol = (active.sectionId ?? null) === targetSection;
      const from = columnOf(targetSection).findIndex((t) => t.id === id);
      if (sameCol && from >= 0 && from <= index) index++;
    }
    const list = columnOf(targetSection).filter((t) => t.id !== id);
    list.splice(Math.max(0, index), 0, active);
    void dispatch(cmd("item_reorder", {
      items: list.map((t, i) => ({ id: t.id, childOrder: i + 1, ...(t.id === id ? { sectionId: targetSection } : {}) })),
    }));
  };

  const addSection = async () => {
    if (!name.trim()) return;
    await dispatch(cmd("section_add", { id: uuid(), projectId, name: name.trim(), sectionOrder: sections.length + 1 }));
    setName(""); setAdding(false);
  };
  const activeTask = activeId ? top.find((t) => t.id === activeId) : undefined;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))} onDragCancel={() => setActiveId(null)} onDragEnd={onDragEnd}>
      <div className="-mx-12 flex snap-x snap-mandatory items-start gap-3 overflow-x-auto px-12 pb-6 max-lg:-mx-8 max-lg:px-8 phone:-mx-4 phone:scroll-px-4 phone:px-4">
        <Column id={NONE} title="No section" tasks={columnOf(null)} counts={counts} projectId={projectId} sectionId={null} dragging={!!activeId} />
        {sorted.map((s) => <Column key={s.id} id={s.id} title={s.name} tasks={columnOf(s.id)} counts={counts} projectId={projectId} sectionId={s.id} section={s} dragging={!!activeId} />)}
        <div className="w-[288px] shrink-0 snap-start phone:w-[85vw]">
          {adding ? (
            <form onSubmit={(e) => { e.preventDefault(); void addSection(); }} className="rounded-card bg-surface-sunken p-2">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Section name" aria-label="Section name" />
              <div className="mt-2 flex gap-2">
                <Button type="submit" size="sm" variant="primary" disabled={!name.trim()}>Add section</Button>
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              </div>
            </form>
          ) : (
            <button type="button" onClick={() => setAdding(true)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-card border border-dashed border-border-strong text-body text-text-muted transition-colors hover:border-brand hover:text-brand-text">
              <Plus size={16} aria-hidden />Add section
            </button>
          )}
        </div>
      </div>
      <DragOverlay>{activeTask && <div className="rotate-1"><TaskCard task={activeTask} counts={counts.get(activeTask.id)} dnd={{ dragging: true }} /></div>}</DragOverlay>
    </DndContext>
  );
}
